import React, { useState, useEffect, useMemo } from "react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getToken } from "../utils/auth";
import {
  Settings,
  Loader2,
  Calendar,
  BookOpen,
  Play,
  Lock,
  Unlock,
  Shield,
  Database,
  Building2,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

export default function HliSelectionComponent() {
  const [completionYear, setCompletionYear] = useState("");
  const [studyArea, setStudyArea] = useState("");
  const [isInitiating, setIsInitiating] = useState(false);
  const [updatingSettingId, setUpdatingSettingId] = useState(null);

  const queryClient = useQueryClient();

  // ── Institution settings → derive filtered academic years & study areas ──────
  const {
    data: institutionSettings = [],
    isLoading: settingsLoading,
    isError: settingsError,
    refetch: refetchSettings,
  } = useQuery({
    queryKey: ["hliMySettingsForSelection"],
    queryFn: async () => {
      const res = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/settings/getSettingsByMyInstitution",
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      return res.data;
    },
    retry: 3,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!getToken(),
    onError: () => {
      toast.error("Failed to load institution settings.", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    },
  });

  // Derive unique academic years from the institution's settings
  const academicYears = useMemo(() => {
    const seen = new Set();
    return institutionSettings
      .filter((s) => {
        const name = s.academicYear?.academicYearName;
        if (!name || seen.has(name)) return false;
        seen.add(name);
        return true;
      })
      .map((s) => ({
        id: s.academicYear.id ?? s.academicYear.academicYearName,
        academicYearName: s.academicYear.academicYearName,
      }));
  }, [institutionSettings]);

  // Derive unique study areas (student categories) from institution's settings
  const studyAreas = useMemo(() => {
    const seen = new Set();
    return institutionSettings
      .filter((s) => {
        if (!s.studentCategory || seen.has(s.studentCategory)) return false;
        seen.add(s.studentCategory);
        return true;
      })
      .map((s) => ({
        id: s.studentCategory,
        studyAreaName: s.studentCategory,
      }));
  }, [institutionSettings]);

  // ── Selection initiation ─────────────────────────────────────────────────────
  const selectionTrigger = async () => {
    if (!completionYear || !studyArea) {
      toast.warning("Please select both completion year and study area", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
      return;
    }
    setIsInitiating(true);
    try {
      await axios.get(
        process.env.REACT_APP_BASE_URL +
          `hec/settings/initiate?completionYearCode=${completionYear}&categoryCode=${studyArea}&key=${process.env.REACT_APP_KEY}`,
        { headers: { Authorization: "Bearer " + getToken() } }
      );
      toast.success("Selection initiation completed successfully!", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    } catch (error) {
      if (error.response?.status === 409) {
        toast.warning("No settings were found for the selected criteria", {
          position: "top-center",
          autoClose: 8000,
          theme: "colored",
        });
      } else {
        toast.error("No local loans found for the given year and category", {
          position: "top-center",
          autoClose: 8000,
          theme: "colored",
        });
      }
    } finally {
      setIsInitiating(false);
    }
  };

  // ── Loan Request Settings ────────────────────────────────────────────────────
  const {
    data: loanSettings,
    isLoading: loanSettingsLoading,
    isError: loanSettingsError,
    error: loanSettingsErrorDetails,
    refetch: refetchLoanSettings,
  } = useQuery({
    queryKey: ["myInstitutionLoanStatus"],
    queryFn: async () => {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + "loanOpened/getMyInstitutionLoanStatus",
        { headers: { Authorization: "Bearer " + getToken() } }
      );
      return response.data;
    },
    retry: 3,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: !!getToken(),
  });

  useEffect(() => {
    if (loanSettingsError && loanSettingsErrorDetails) {
      console.error("Loan settings query error:", loanSettingsErrorDetails);
    }
  }, [loanSettingsError, loanSettingsErrorDetails]);

  const updateLoanSettingMutation = useMutation({
    mutationFn: async ({ institutionId, newStatus }) => {
      const response = await axios.put(
        process.env.REACT_APP_BASE_URL +
          `loanOpened/updateLoanStatusByInstitution?institutionId=${institutionId}&status=${newStatus}`,
        {},
        {
          headers: {
            Authorization: "Bearer " + getToken(),
            "Content-Type": "application/json",
          },
        }
      );
      return { ...response.data, institutionId, newStatus };
    },
    onMutate: async (variables) => {
      setUpdatingSettingId(variables.institutionId);
      await queryClient.cancelQueries(["myInstitutionLoanStatus"]);
      const previousSettings = queryClient.getQueryData(["myInstitutionLoanStatus"]);
      queryClient.setQueryData(["myInstitutionLoanStatus"], (old) => {
        if (!old) return old;
        return { ...old, loanRequestOpened: variables.newStatus };
      });
      return { previousSettings };
    },
    onSuccess: (data, variables) => {
      toast.success(
        `Loan request ${variables.newStatus ? "opened" : "closed"} successfully!`,
        { position: "top-center", autoClose: 3000, theme: "colored" }
      );
    },
    onError: (error, variables, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(["myInstitutionLoanStatus"], context.previousSettings);
      }
      toast.error("Failed to update loan setting", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    },
    onSettled: (data, error, variables) => {
      setUpdatingSettingId(null);
      queryClient.invalidateQueries(["myInstitutionLoanStatus"]);
    },
  });

  const handleStatusUpdate = (institutionId, currentStatus) => {
    updateLoanSettingMutation.mutate({ institutionId, newStatus: !currentStatus });
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="bg-slate-100 min-h-screen flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>

      <div className="p-6 sm:ml-64 transition-all duration-300 flex-1 flex flex-col">
        {/* Page Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Settings className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Selection & Loan Management</h1>
                  <p className="text-gray-600 mt-1">
                    Manage selection criteria and loan request settings for your institution
                  </p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Building2 className="w-4 h-4 text-cyan-600" />
                    <span className="text-sm font-medium text-cyan-700">
                      Showing options scoped to your institution
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => refetchSettings()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-cyan-700 transition-colors self-start sm:self-auto"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">

            {/* ── Selection Criteria Section ─────────────────────────────── */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
              {/* Form Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="w-8 h-8 text-cyan-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Selection Criteria</h2>
                <p className="text-gray-600">
                  Select completion year and study area to initiate the selection process
                </p>
              </div>

              {settingsLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mb-3" />
                  <p className="text-sm">Loading your institution settings...</p>
                </div>
              ) : settingsError ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500" />
                  </div>
                  <p className="text-sm font-medium text-red-600 mb-1">Failed to load institution settings</p>
                  <p className="text-xs text-gray-500 mb-4">Dropdowns cannot be populated without settings.</p>
                  <button
                    onClick={() => refetchSettings()}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Retry
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Completion Year */}
                  <div>
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                      <Calendar className="w-4 h-4 mr-2 text-cyan-600" />
                      Completion Year
                    </label>
                    <select
                      onChange={(e) => setCompletionYear(e.target.value)}
                      value={completionYear}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                    >
                      <option value="">Select completion year</option>
                      {academicYears.map((ay) => (
                        <option key={ay.id} value={ay.academicYearName}>
                          {ay.academicYearName}
                        </option>
                      ))}
                    </select>
                    {academicYears.length === 0 && !settingsLoading && (
                      <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        No academic years found for your institution.
                      </p>
                    )}
                  </div>

                  {/* Study Area */}
                  <div>
                    <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                      <BookOpen className="w-4 h-4 mr-2 text-cyan-600" />
                      Study Area
                    </label>
                    <select
                      onChange={(e) => setStudyArea(e.target.value)}
                      value={studyArea}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                    >
                      <option value="">Select study area</option>
                      {studyAreas.map((area) => (
                        <option key={area.id} value={area.studyAreaName}>
                          {area.studyAreaName}
                        </option>
                      ))}
                    </select>
                    {studyAreas.length === 0 && !settingsLoading && (
                      <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        No study areas found for your institution.
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      onClick={selectionTrigger}
                      disabled={isInitiating || !completionYear || !studyArea}
                      className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-4 px-6 rounded-xl font-semibold text-lg 
                               hover:from-cyan-700 hover:to-blue-700 transition-all duration-200 transform hover:scale-[1.02] 
                               disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                               shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                    >
                      {isInitiating ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-5 h-5" />
                          <span>Initiate Selection</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Info Card */}
              <div className="mt-8 bg-cyan-50 border border-cyan-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Building2 className="w-3 h-3 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-cyan-800 mb-1">Institution-Scoped Selection</p>
                    <p className="text-sm text-cyan-700">
                      The completion years and study areas shown are restricted to your institution's
                      configured settings. Only categories assigned to your institution are available
                      for selection.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Loan Request Settings Section ──────────────────────────── */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
              {/* Settings Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Loan Request Settings</h2>
                <p className="text-gray-600">Manage loan request opening and closing status</p>
              </div>

              {/* Settings List */}
              <div className="space-y-4">
                {loanSettingsLoading ? (
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 animate-spin" />
                    </div>
                    <p className="text-gray-600 text-base sm:text-lg font-medium mb-1 sm:mb-2">Loading Settings</p>
                    <p className="text-gray-500 text-sm px-4">Please wait while we fetch the loan request settings...</p>
                  </div>
                ) : loanSettingsError ? (
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Database className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
                    </div>
                    <p className="text-red-600 text-base sm:text-lg font-medium mb-1 sm:mb-2">Failed to Load Settings</p>
                    <p className="text-gray-500 text-sm px-4 mb-4">There was an error loading the loan request settings.</p>
                    <button
                      onClick={() => refetchLoanSettings()}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span>Retry</span>
                    </button>
                  </div>
                ) : loanSettings ? (
                    <div
                      className="border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                        <div className="flex items-center space-x-3 sm:space-x-4 flex-1">
                          <div
                            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              loanSettings.loanRequestOpened
                                ? "bg-gradient-to-r from-green-500 to-emerald-500"
                                : "bg-gradient-to-r from-red-500 to-pink-500"
                            }`}
                          >
                            {loanSettings.loanRequestOpened ? (
                              <Unlock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            ) : (
                              <Lock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                              {loanSettings.institution?.fullName || "My Institution"} — Loan Status
                            </h3>
                            <div className="flex items-center justify-between sm:justify-start sm:space-x-4 mt-1">
                              {/* Status Badge */}
                              <span
                                className={`inline-flex items-center space-x-1 sm:space-x-2 px-2 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold ${
                                  loanSettings.loanRequestOpened
                                    ? "bg-green-100 text-green-800 border border-green-200"
                                    : "bg-red-100 text-red-800 border border-red-200"
                                }`}
                              >
                                {loanSettings.loanRequestOpened ? (
                                  <><Unlock className="w-3 h-3 sm:w-4 sm:h-4" /><span>Open</span></>
                                ) : (
                                  <><Lock className="w-3 h-3 sm:w-4 sm:h-4" /><span>Closed</span></>
                                )}
                              </span>

                              {/* Toggle Button */}
                              <button
                                onClick={() =>
                                  handleStatusUpdate(
                                    loanSettings.institution?.id,
                                    loanSettings.loanRequestOpened
                                  )
                                }
                                disabled={
                                  !loanSettings.institution?.id ||
                                  updatingSettingId === loanSettings.institution?.id
                                }
                                className={`relative inline-flex h-8 w-14 sm:h-12 sm:w-20 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                  loanSettings.loanRequestOpened
                                    ? "bg-green-500 focus:ring-green-500"
                                    : "bg-red-500 focus:ring-red-500"
                                } disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0`}
                              >
                                <span className="sr-only">Toggle loan request status</span>
                                <div
                                  className={`inline-block h-6 w-6 sm:h-8 sm:w-8 transform rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ${
                                    loanSettings.loanRequestOpened
                                      ? "translate-x-6 sm:translate-x-10"
                                      : "translate-x-1"
                                  }`}
                                >
                                  {updatingSettingId === loanSettings.institution?.id ? (
                                    <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin text-gray-600 m-1.5 sm:m-2" />
                                  ) : loanSettings.loanRequestOpened ? (
                                    <Unlock className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 m-1.5 sm:m-2" />
                                  ) : (
                                    <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-red-600 m-1.5 sm:m-2" />
                                  )}
                                </div>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="mt-4 pl-0 sm:pl-16">
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                          {loanSettings.loanRequestOpened
                            ? "Students can currently submit new loan requests. Click the toggle to close loan submissions."
                            : "Loan requests are currently closed. Students cannot submit new applications. Click the toggle to open submissions."}
                        </p>
                      </div>
                    </div>
                ) : (
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <Database className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600 text-base sm:text-lg font-medium mb-1 sm:mb-2">No Settings Found</p>
                    <p className="text-gray-500 text-sm px-4">No loan request settings are currently configured.</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        <FooterComponents />
      </div>
    </div>
  );
}

